import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { JobOffer } from "@ckgo2/shared/types";
import { useEditPage } from "@/hooks/useEditPage";
import EditPageHeader from "@/components/common/EditPageHeader";
import MediaPicker from "@/components/common/MediaPicker";

interface JobOfferFormData {
  title: string;
  description: string;
  imageId: string | null;
  backgroundImageId: string | null;
  linkUrl: string;
  publishedDate: string;
  sortOrder: number;
  status: string;
}

export default function JobOfferEditPage() {
  const { t } = useTranslation();
  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<
    JobOfferFormData,
    JobOffer
  >({
    resourceKey: "job-offer",
    listKey: "job-offers",
    apiPath: "/api/admin/job-offers",
    defaultValues: {
      title: "",
      description: "",
      imageId: null,
      backgroundImageId: null,
      linkUrl: "",
      publishedDate: new Date().toISOString().slice(0, 10),
      sortOrder: 0,
      status: "published",
    },
    navigateTo: "/job-offers",
    transformOnLoad: (o) => ({
      title: o.title || "",
      description: o.description || "",
      imageId: o.imageId ?? null,
      backgroundImageId: o.backgroundImageId ?? null,
      linkUrl: o.linkUrl || "",
      publishedDate: o.publishedDate || "",
      sortOrder: o.sortOrder || 0,
      status: o.status || "published",
    }),
    transformOnSave: (data) => ({
      ...data,
      // Preserve null for nullable DB columns (empty string would fail nothing
      // here, but null keeps the DB clean and the web fallback logic simple).
      linkUrl: data.linkUrl || null,
      publishedDate: data.publishedDate || null,
    }),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = form;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EditPageHeader
        backUrl="/job-offers"
        isNew={isNew}
        newTitle={t("content.jobOffers.form.newTitle")}
        editTitle={t("content.jobOffers.form.editTitle")}
        entityName={itemData?.title}
        formId="job-offer-form"
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        error={saveMutation.error}
        isSuccess={saveMutation.isSuccess}
      />

      <form
        id="job-offer-form"
        onSubmit={handleSubmit(onSubmit)}
        className="grid gap-6 lg:grid-cols-3"
      >
        <div className="space-y-6 lg:col-span-2">
          <div className="card space-y-4">
            <div>
              <label className="label">{t("common.fields.title")}</label>
              <input
                {...register("title", { required: t("content.shared.titleRequired") })}
                className="input"
                placeholder={t("content.jobOffers.form.titlePlaceholder")}
              />
              {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title.message}</p>}
            </div>

            <div>
              <label className="label">{t("common.fields.description")}</label>
              <textarea
                {...register("description", {
                  required: t("content.jobOffers.form.descriptionRequired"),
                })}
                className="input min-h-24"
                rows={3}
                placeholder={t("content.jobOffers.form.descriptionPlaceholder")}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>
              )}
            </div>

            <div>
              <label className="label">{t("content.jobOffers.form.linkLabel")}</label>
              <input
                {...register("linkUrl")}
                className="input"
                placeholder={t("content.jobOffers.form.linkPlaceholder")}
              />
              <p className="mt-1 text-sm text-gray-500">{t("content.jobOffers.form.linkHint")}</p>
            </div>
          </div>

          <div className="card grid gap-4 md:grid-cols-2">
            <div>
              <label className="label">{t("content.jobOffers.form.cardPhotoLabel")}</label>
              <Controller
                name="imageId"
                control={control}
                render={({ field }) => (
                  <MediaPicker
                    mode="single"
                    value={field.value ?? null}
                    onChange={field.onChange}
                    folder="general"
                  />
                )}
              />
            </div>
            <div>
              <label className="label">{t("content.jobOffers.form.backgroundLabel")}</label>
              <Controller
                name="backgroundImageId"
                control={control}
                render={({ field }) => (
                  <MediaPicker
                    mode="single"
                    value={field.value ?? null}
                    onChange={field.onChange}
                    folder="general"
                  />
                )}
              />
              <p className="mt-1 text-sm text-gray-500">
                {t("content.jobOffers.form.backgroundHint")}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card space-y-4">
            <div>
              <label className="label">{t("content.jobOffers.form.publishedDateLabel")}</label>
              <input type="date" {...register("publishedDate")} className="input" />
            </div>
            <div>
              <label className="label">{t("common.fields.sortOrder")}</label>
              <input
                type="number"
                {...register("sortOrder", { valueAsNumber: true })}
                className="input"
              />
            </div>
            <div>
              <label className="label">{t("common.fields.status")}</label>
              <select {...register("status")} className="input">
                <option value="published">{t("common.status.published")}</option>
                <option value="draft">{t("common.status.draft")}</option>
              </select>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
